import {Helmet} from "react-helmet";
import {Button, makeStyles} from "@material-ui/core";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell/TableCell";
import {useCallback, useEffect, useState} from "react";
import ListDotMenu from "./ListDotMenu";
import RenameList from "./RenameList";
import DeleteList from "./DeleteList";
import {useDispatch, useSelector} from "react-redux";
import CreateList from "./CreateList";
import {startParticipantListCreate} from "../../../../store/actions";
import {getParticipantList, initListDetailData} from "../../../../store/actions/participant/list";
import {BackDropAnalyzeLoading} from "../../../atoms/commonLoadings";
import UtilService from "../../../../helper/service";
import {Prompt, useHistory} from 'react-router-dom';
import {
  getSelectedListsParticipants,
  updateUserMetrics
} from "../../../../store/actions/creator/creatorExperienceBuilder";
import CreatorCloseBtnModal from "../../../atoms/modals/creatorCloseBtnModal";
import CustomCheckBox from "../../creator/experienceManager/participantsDashboard/component/CustomCheckbox";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: '#F0F0F0',
    paddingTop: "30px",
    fontFamily: 'roboto-regular',
    height: '100%',
    overflowX: 'hidden',
  },
  content: {
    minHeight: '100%',
    padding: '0 32px 100px 32px',
    width: '100%',
    '@media (min-width: 2000px)': {
      width: 1600
    },
    '@media (max-width: 600px)': {
      padding: '55px 10px 10px 10px'
    },
  },
  paper: {
    width: '100%',
    marginBottom: '16px',
    paddingBottom: '24px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: 10,
    color: '#263238',
    backgroundColor: '#fff'
  },
  table: {
    minWidth: 750,
    '@media (max-width: 600px)': {
      width: '100%',
      minWidth: 'unset'
    }
  },
  tableHeadText: {
    fontFamily: 'roboto-bold',
    fontSize: 12,
    textTransform: 'uppercase',
    lineHeight: 1.5,
    color: '#606B76',
    '@media (max-width: 1025px)': {
      fontSize: 10
    }
  },
  tableRowNormal: {
    fontFamily: 'roboto-regular',
    fontSize: 14,
    color: '#606B76',
    backgroundColor: '#FFFFFF !important',
  },
  tableRowHover: {
    backgroundColor: '#F2F7FC !important'
  },
  tableRowSelected: {
    backgroundColor: '#F2F7FC !important'
  },
  tableRoot: {
    width: '100%',
    paddingBottom: 80,
    position: 'relative'
  },
  cursorPointer: {
    cursor: 'pointer'
  },
  actionBtnDiv: {
    position: 'absolute',
    right: 0
  },
  addBtn: {
    flex: 1,
    textTransform: "unset",
    backgroundColor: '#017AEF',
    color: '#fff',
    paddingLeft: 50,
    paddingRight: 50,
    marginLeft: 20,
    "&:hover": {
      backgroundColor: '#0f9fef',
    }
  },
  cancelBtn: {
    flex: 1,
    textTransform: "unset",
    backgroundColor: '#F0F0F0',
    color: '#697683',
    "&:hover": {
      backgroundColor: '#e0e0e0',
    }
  }
}));

interface IHeaderCell {
  disablePadding?: boolean;
  id?: string;
  label?: string;
  numeric?: boolean;
  color?: string;
}

const headerCells: IHeaderCell[] = [
  { id: 'list_id', numeric: false, disablePadding: true, label: '', color: 'primary' },
  { id: 'list_name', numeric: false, disablePadding: false, label: 'LIST NAME', color: 'primary' },
  { id: 'participants', numeric: false, disablePadding: false, label: 'PARTICIPANTS', color: 'default' },
  { id: 'date_created', numeric: false, disablePadding: false, label: 'DATE CREATED', color: 'default' },
  // { id: 'date_last_used', numeric: false, disablePadding: false, label: 'DATE LAST USED', color: 'default' },
  { id: 'action_btn', numeric: false, disablePadding: false, label: '', color: 'default' },
];

interface IProps {
  onActionBtnClicked?: () => void
}

const ParticipantLists = (props: IProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const apolloClient = useSelector((state: any) => state.apolloClient);
  const { onActionBtnClicked } = props;
  const { startNewList, participantLists, listLoading } = useSelector((state: any) => state.participantList);
  const { userMetrics } = useSelector((state: any) => state.creatorExperienceBuilder);
  const { addParticipantList } = userMetrics;

  const [hoverListId, setHoverListId] = useState('');
  const [selectedList, setSelectedList] = useState(null);
  const [openRenameModal, setOpenRenameModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [confirmationLeavePage, setConfirmationLeavePage] = useState(false);

  const [checkedLists, setCheckedLists] = useState<any[]>([]);

  useEffect(() => {
    dispatch(updateUserMetrics('listsParticipants', []));
    dispatch(initListDetailData());
    dispatch(getParticipantList(apolloClient.client, true));
  }, []);

  const onRenameList = (list: any) => {
    setSelectedList(list);
    setOpenRenameModal(true);
  };

  const onDeleteList = (list: any) => {
    setSelectedList(list);
    setOpenDeleteModal(true);
  };

  const onCloseModal = () => {
    setSelectedList(null);
    setOpenRenameModal(false);
    setOpenDeleteModal(false);
    dispatch(startParticipantListCreate(false));
  };

  const goDetail = useCallback((list: any) => {
    if (!addParticipantList) {
      const url = `/participant/list/${list.group_id}/detail`;
      history.push(url, {listName: list.group_name, listEditable: list.editable});
    }
  }, [addParticipantList]);

  const onCancel = () => {
    onActionBtnClicked && onActionBtnClicked();
    dispatch(updateUserMetrics('addParticipantList', false));
  };

  const onAddParticipantsToExperience = () => {
    onActionBtnClicked && onActionBtnClicked();
    const group_ids = checkedLists.map((cl: any) => cl.group_id);
    const request = { group_ids };
    dispatch(getSelectedListsParticipants(apolloClient.client, request));
  };

  const handleBlockedNavigation = (e: any) => {
    if (e.pathname.includes('/creator/experience-builder') || e.pathname.includes('/creator/experience-manager/invite')) {
      return true;
    }
    if (addParticipantList) {
      setConfirmationLeavePage(e.pathname);
      return false
    } else {
      setConfirmationLeavePage(false);
      return true;
    }
  };

  const onCloseLeaveModal = () => {
    setConfirmationLeavePage(false);
  };

  const onApply = () => {
    dispatch(updateUserMetrics('addParticipantList', false));
  };

  const isListChecked = useCallback((list_id: string) => {
    const checkedList = checkedLists.find((l: any) => l.group_id === list_id);
    return !!checkedList;
  }, [checkedLists]);

  const handleListCheck = useCallback((list: any) => {
    const _list = checkedLists.find((l: any) => l.group_id === list.group_id);
    if (_list) {
      const newCheckedLists =
        checkedLists.filter((l: any) => l.group_id != list.group_id);
      setCheckedLists(newCheckedLists);
    } else {
      const originalLists = [...checkedLists];
      originalLists.push(list);
      setCheckedLists(originalLists);
    }
  }, [checkedLists]);

  return (
    <div className={classes.root}>
      <Helmet title={addParticipantList ? 'Experience Builder' : 'Participant Lists'} />
      <BackDropAnalyzeLoading loading={listLoading} />
      <div className={classes.content}>
        <div className={classes.tableRoot}>
          <div className={classes.paper}>
            <TableContainer>
              <Table
                className={classes.table}
                aria-labelledby="tableTitle"
                size="medium"
                aria-label="enhanced table"
              >
                <TableHead>
                  <TableRow>
                    {headerCells.map((header: any) => (
                      <TableCell
                        key={header.id}
                        align="left"
                        padding="normal"
                        className={classes.tableHeadText}
                      >
                        <span>{header.label}</span>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participantLists.map((list: any, index: number) => (
                    <TableRow
                      onMouseOver={() => setHoverListId(list.group_id)}
                      onMouseLeave={() => setHoverListId('')}
                      role="checkbox"
                      tabIndex={-1}
                      key={list.group_id}
                      hover={list.group_id === hoverListId}
                      classes={{
                        root: classes.tableRowNormal,
                        hover: classes.tableRowHover,
                        selected: classes.tableRowSelected,
                      }}
                    >
                      <TableCell className={classes.cursorPointer} padding="checkbox" onClick={() => goDetail(list)}>
                        {addParticipantList && (
                          <CustomCheckBox
                            key={`check-box-${list.group_id}`}
                            checked={isListChecked(list.group_id)}
                            onClick={() => handleListCheck(list)}
                            inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${index}` }}
                          />
                        )}
                      </TableCell>
                      <TableCell className={classes.cursorPointer} scope="row" align="left" onClick={() => goDetail(list)}>
                        { list.group_name }
                      </TableCell>
                      <TableCell className={classes.cursorPointer} scope="row" align="left" onClick={() => goDetail(list)}>
                        { list.total_participants_count }
                      </TableCell>
                      <TableCell className={classes.cursorPointer} scope="row" align="left" onClick={() => goDetail(list)}>
                        { UtilService.getParticipantListDate(list.created_at) }
                      </TableCell>
                      {/*<TableCell className={classes.cursorPointer} scope="row" align="left" onClick={() => goDetail(list)}>*/}
                      {/*  { UtilService.getParticipantListDate(list.updated_at) }*/}
                      {/*</TableCell>*/}
                      <TableCell align="left" padding='none'>
                        {(hoverListId === list.group_id && !addParticipantList && list.editable) && (
                          <ListDotMenu
                            key={`dot-menu-${list.group_id}`}
                            onRenameList={() => onRenameList(list)}
                            onDeleteList={() => onDeleteList(list)}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          {addParticipantList && (
            <div className={classes.actionBtnDiv}>
              <Button variant="contained" className={classes.cancelBtn} onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className={classes.addBtn}
                onClick={onAddParticipantsToExperience}
                disabled={checkedLists.length === 0}
              >
                Add
              </Button>
            </div>
          )}
        </div>
      </div>
      <CreateList
        open={startNewList}
        onClose={onCloseModal}
      />
      <RenameList
        open={openRenameModal}
        onClose={onCloseModal}
        selectedList={selectedList}
      />
      <DeleteList
        open={openDeleteModal}
        onClose={onCloseModal}
        selectedList={selectedList}
      />

      <CreatorCloseBtnModal
        open={confirmationLeavePage}
        onClose={onCloseLeaveModal}
        onApply={onApply}
      />
      <Prompt
        when={addParticipantList}
        message={handleBlockedNavigation}
      />
    </div>
  )
};

export default ParticipantLists;